import { useEffect, useState, useCallback } from "react";
import api, { API_BASE, formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Link } from "react-router-dom";
import { FileDown, RefreshCw, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const STATUS_LABEL = {
  paid: { text: "Pagato", cls: "bg-emerald-50 text-emerald-800 border-emerald-200" },
  unpaid: { text: "Non pagato", cls: "bg-amber-50 text-amber-900 border-amber-200" },
  no_payment_required: { text: "N/A", cls: "bg-zinc-50 text-zinc-700 border-zinc-200" },
};

export default function Fatturazione() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const today = new Date();
  const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().slice(0, 10);
  const todayStr = today.toISOString().slice(0, 10);
  const [fromDate, setFromDate] = useState(firstOfMonth);
  const [toDate, setToDate] = useState(todayStr);
  const [fStatus, setFStatus] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { from_date: fromDate, to_date: toDate + "T23:59:59" };
      if (fStatus !== "all") params.payment_status = fStatus;
      const { data } = await api.get("/payments/transactions", { params });
      setData(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [fromDate, toDate, fStatus]);

  useEffect(() => { load(); }, [load]);

  const deleteRow = async (sessionId, quoteNumber) => {
    if (!window.confirm(
      `Eliminare la riga di fatturazione ${quoteNumber || sessionId.slice(0, 20) + '…'}?\n\n` +
      `Non verrà tolta la transazione da Stripe: viene rimossa solo la traccia locale nell'app. ` +
      `Se il pagamento era stato incassato, ricordati che il preventivo collegato resta segnato come pagato.`
    )) return;
    setDeletingId(sessionId);
    try {
      await api.delete(`/payments/transactions/${encodeURIComponent(sessionId)}`);
      toast.success("Riga fatturazione eliminata");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setDeletingId(null); }
  };

  const downloadCsv = () => {
    const params = new URLSearchParams();
    if (fromDate) params.set("from_date", fromDate);
    if (toDate) params.set("to_date", toDate + "T23:59:59");
    if (fStatus !== "all") params.set("payment_status", fStatus);
    window.open(`${API_BASE}/payments/transactions.csv?${params.toString()}`, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="p-4 sm:p-8 max-w-7xl" data-testid="fatturazione-page">
      <PageHeader
        title="Fatturazione"
        description="Pagamenti Stripe ricevuti per preventivi fuori garanzia. Filtra per periodo e stato, esporta in CSV per la contabilità."
        actions={
          <Button data-testid="fatt-csv-btn" onClick={downloadCsv} className="bg-brand hover:bg-brand-dark text-white rounded-sm">
            <FileDown className="h-4 w-4 mr-2" /> Esporta CSV
          </Button>
        }
      />

      {/* Filtri */}
      <Card className="rounded-sm border-zinc-200 bg-white p-5 mb-5">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <Label className="text-xs uppercase tracking-widest text-zinc-500">Dal</Label>
            <Input data-testid="fatt-from" type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="mt-1 rounded-sm" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-zinc-500">Al</Label>
            <Input data-testid="fatt-to" type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="mt-1 rounded-sm" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-zinc-500">Stato pagamento</Label>
            <Select value={fStatus} onValueChange={setFStatus}>
              <SelectTrigger data-testid="fatt-status" className="mt-1 rounded-sm"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti</SelectItem>
                <SelectItem value="paid">Pagati</SelectItem>
                <SelectItem value="unpaid">Non pagati</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end">
            <Button data-testid="fatt-refresh" variant="outline" onClick={load} disabled={loading} className="rounded-sm">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            </Button>
          </div>
        </div>
      </Card>

      {/* KPI riepilogo */}
      {data && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
          <Card className="rounded-sm border-zinc-200 bg-white p-5">
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Totale transazioni</div>
            <div className="mt-2 font-display text-3xl font-bold text-zinc-900" data-testid="fatt-count">{data.count}</div>
          </Card>
          <Card className="rounded-sm border-emerald-200 bg-emerald-50/50 p-5">
            <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-800">Pagati nel periodo</div>
            <div className="mt-2 font-display text-3xl font-bold text-emerald-900" data-testid="fatt-total-paid">
              € {data.total_paid.toLocaleString("it-IT", { minimumFractionDigits: 2 })}
            </div>
            <div className="text-xs text-emerald-700 mt-1">{data.count_paid} transazioni</div>
          </Card>
          <Card className="rounded-sm border-zinc-200 bg-white p-5">
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Volume totale (incl. non pagati)</div>
            <div className="mt-2 font-display text-3xl font-bold text-zinc-900" data-testid="fatt-total-all">
              € {data.total_all.toLocaleString("it-IT", { minimumFractionDigits: 2 })}
            </div>
          </Card>
        </div>
      )}

      {/* Tabella transazioni */}
      <Card className="rounded-sm border-zinc-200 p-0 overflow-hidden bg-white">
        <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[900px]">
          <thead>
            <tr className="text-[10px] uppercase tracking-widest text-zinc-500 border-b border-zinc-200">
              <th className="text-left px-5 py-3 font-medium">Data</th>
              <th className="text-left px-5 py-3 font-medium">Preventivo</th>
              <th className="text-left px-5 py-3 font-medium">Ticket</th>
              <th className="text-left px-5 py-3 font-medium">Cliente</th>
              <th className="text-right px-5 py-3 font-medium">Importo</th>
              <th className="text-center px-5 py-3 font-medium">Stato</th>
              <th className="text-left px-5 py-3 font-medium">Session ID</th>
              {isAdmin && <th className="text-right px-5 py-3 font-medium">Azioni</th>}
            </tr>
          </thead>
          <tbody>
            {(!data || data.transactions.length === 0) && (
              <tr><td colSpan={isAdmin ? 8 : 7} className="p-10 text-center text-zinc-500">Nessuna transazione in questo periodo.</td></tr>
            )}
            {data && data.transactions.map((t) => {
              const st = STATUS_LABEL[t.payment_status] || { text: t.payment_status, cls: "bg-zinc-100 text-zinc-700 border-zinc-200" };
              return (
                <tr key={t.session_id} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`fatt-row-${t.session_id}`}>
                  <td className="px-5 py-3 text-zinc-700 font-mono text-xs">{(t.created_at || "").slice(0, 16).replace("T", " ")}</td>
                  <td className="px-5 py-3 text-zinc-900 font-mono text-xs">{t.quote_number || "—"}</td>
                  <td className="px-5 py-3">
                    {t.ticket_id ? (
                      <Link to={`/tickets/${t.ticket_id}`} className="text-brand font-mono text-xs hover:underline">
                        {(t.metadata || {}).ticket_number || t.ticket_id.slice(0, 8)}
                      </Link>
                    ) : <span className="text-zinc-400">—</span>}
                  </td>
                  <td className="px-5 py-3 text-zinc-700">{t.customer_name || "—"}</td>
                  <td className="px-5 py-3 text-right font-mono text-zinc-900">
                    € {t.amount?.toLocaleString("it-IT", { minimumFractionDigits: 2 })}
                  </td>
                  <td className="px-5 py-3 text-center">
                    <Badge className={`rounded-sm text-[10px] uppercase tracking-widest border ${st.cls}`}>{st.text}</Badge>
                  </td>
                  <td className="px-5 py-3 text-zinc-500 font-mono text-[10px] truncate max-w-xs" title={t.session_id}>
                    {t.session_id.slice(0, 24)}…
                  </td>
                  {isAdmin && (
                    <td className="px-5 py-3 text-right">
                      <button
                        onClick={() => deleteRow(t.session_id, t.quote_number)}
                        disabled={deletingId === t.session_id}
                        className="inline-flex items-center gap-1 h-8 px-2 rounded-sm border border-red-200 text-xs text-red-700 hover:bg-red-50 disabled:opacity-50"
                        data-testid={`fatt-delete-${t.session_id}`}
                        title="Elimina riga (admin)"
                      >
                        {deletingId === t.session_id
                          ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          : <Trash2 className="h-3.5 w-3.5" />}
                      </button>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
      </Card>
    </div>
  );
}
